import { Pipe, PipeTransform } from '@angular/core';
import { EmployeeEntity } from '../../store/entities/EmployeeEntity';
import { EmployeeTeamleaderAssingmentEntity } from '../../store/entities/EmployeeTeamleaderAssingmentEntity';

/**
 * Prepares the member rows of one teamleader group for display.
 *
 * While a search is active the group is reduced to the members that match it, so an expanded
 * group shows only the person that was searched for instead of its full roster. Terms are matched
 * individually against first and last name, which is what lets "marc jacob" match a person whose
 * name is split across two fields.
 *
 * Without a search every member is kept. In both cases the rows are ordered by last name — the
 * webservice returns them unordered, which was the recurring complaint.
 *
 * Pure on purpose: the rows carry drag & drop and checkboxes, and recomputing them on every
 * change detection cycle would rebuild the array each time and make the list flicker.
 */
@Pipe({
	name: 'teamMembers',
	pure: true,
})
export class TeamMembersPipe implements PipeTransform {
	transform(
		assignments: EmployeeTeamleaderAssingmentEntity[],
		employees: EmployeeEntity[],
		search: string
	): EmployeeTeamleaderAssingmentEntity[] {
		if (!assignments) {
			return [];
		}

		const employeeOf = (assignment: EmployeeTeamleaderAssingmentEntity) =>
			employees?.find((employee) => +employee.persnr === assignment.persnr);

		const terms = (search ?? '')
			.toLowerCase()
			.split(/\s+/)
			.filter(Boolean);

		const visible = terms.length
			? assignments.filter((assignment) => {
					const employee = employeeOf(assignment);

					if (!employee) {
						return false;
					}

					const fields = [employee.firstname ?? '', employee.lastname ?? ''].map((f) => f.toLowerCase());

					return terms.every((term) => fields.some((field) => field.includes(term)));
			  })
			: assignments.slice();

		return visible.sort((a, b) => {
			const nameA = employeeOf(a);
			const nameB = employeeOf(b);

			return `${nameA?.lastname ?? ''} ${nameA?.firstname ?? ''}`.localeCompare(
				`${nameB?.lastname ?? ''} ${nameB?.firstname ?? ''}`
			);
		});
	}
}
